import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { forwardRef, memo, useEffect, useRef, useState } from 'react';

import * as THREE from 'three';

import WebGLErrorBoundary, {
  isWebGLAvailable
} from 'src/shared/components/WebGLErrorBoundary';

interface IntroSupplierLoaderProps {
  className?: string;
  speedMultiplier?: number;
  initialCameraPosition?: {
    x?: number;
    y?: number;
    z?: number;
  };
  numGlobes?: number;
}

// Seeded random number generator for consistent positioning
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;

    return this.seed / 233280;
  }
}

// Helper function to get optimal FPS based on device capabilities
const getOptimalFPS = (): number => {
  // Check for low-end devices
  const nav = navigator as Navigator & { deviceMemory?: number };
  const isLowEndDevice =
    navigator.hardwareConcurrency <= 4 ||
    (nav.deviceMemory && nav.deviceMemory <= 4) ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // Cap FPS at 30 for low-end devices, 60 for others
  return isLowEndDevice ? 30 : 60;
};

// LOD Scene component that handles camera animation and LOD objects
interface LODSceneProps {
  speedMultiplier: number;
  speedMultiplierRef: React.MutableRefObject<number>;
  isVisibleRef: React.MutableRefObject<boolean>;
  isTabVisibleRef: React.MutableRefObject<boolean>;
  initialCameraPosition?: {
    x?: number;
    y?: number;
    z?: number;
  };
  numGlobes: number;
}

const LODScene = memo(
  ({
    speedMultiplier,
    speedMultiplierRef,
    isVisibleRef,
    isTabVisibleRef,
    initialCameraPosition,
    numGlobes
  }: LODSceneProps) => {
    const { camera, scene, gl } = useThree();
    const lodObjectsRef = useRef<THREE.LOD[]>([]);
    const geometriesRef = useRef<THREE.IcosahedronGeometry[]>([]);
    const materialRef = useRef<THREE.MeshLambertMaterial | null>(null);
    const startTimeRef = useRef<number>(Date.now());
    const lastFrameTimeRef = useRef<number>(0);
    const targetFPS = getOptimalFPS();
    const frameInterval = 1000 / targetFPS;
    const [contextRestoreKey, setContextRestoreKey] = useState(0);

    // Set scene fog (only once, don't recreate)
    useEffect(() => {
      if (!scene.fog) {
        scene.fog = new THREE.Fog(0xffffff, 1, 15000);
      }

      return () => {
        // Clean up fog
        if (scene.fog) {
          scene.fog = null;
        }
      };
    }, [scene]);

    // WebGL context recovery handlers
    useEffect(() => {
      if (!gl?.domElement) return;

      const handleContextLost = (event: Event) => {
        event.preventDefault();
        console.warn('WebGL context lost, attempting recovery...');
      };

      const handleContextRestored = () => {
        console.log('WebGL context restored, reinitializing...');
        // Clean up old resources using stored refs
        lodObjectsRef.current.forEach((lod) => {
          if (scene) {
            scene.remove(lod);
          }
          lod.traverse((object) => {
            if (object instanceof THREE.Mesh) {
              object.geometry = null as any;
              object.material = null as any;
            }
          });
        });
        lodObjectsRef.current = [];

        // Dispose geometries and material from refs
        geometriesRef.current.forEach((geo) => geo.dispose());
        geometriesRef.current = [];
        if (materialRef.current) {
          materialRef.current.dispose();
          materialRef.current = null;
        }

        // Force re-initialization by updating state
        setContextRestoreKey((prev) => prev + 1);
      };

      const canvas = gl.domElement;

      canvas.addEventListener('webglcontextlost', handleContextLost);
      canvas.addEventListener('webglcontextrestored', handleContextRestored);

      return () => {
        canvas.removeEventListener('webglcontextlost', handleContextLost);
        canvas.removeEventListener(
          'webglcontextrestored',
          handleContextRestored
        );
      };
    }, [gl, scene]);

    // Initialize LOD objects
    useEffect(() => {
      // Clean up previous LOD objects properly
      lodObjectsRef.current.forEach((lod) => {
        if (scene) {
          scene.remove(lod);
        }
        // Traverse and clean up meshes
        lod.traverse((object) => {
          if (object instanceof THREE.Mesh) {
            // Clear geometry and material references
            object.geometry = null as any;
            object.material = null as any;
          }
        });
      });
      lodObjectsRef.current = [];

      // Dispose previous geometries
      geometriesRef.current.forEach((geo) => {
        geo.dispose();
      });
      geometriesRef.current = [];

      // Dispose previous material
      if (materialRef.current) {
        materialRef.current.dispose();
        materialRef.current = null;
      }

      // Geometry levels for LOD - create once and share across all LOD objects
      const geometry: Array<[THREE.IcosahedronGeometry, number]> = [
        [new THREE.IcosahedronGeometry(100, 16), 50],
        [new THREE.IcosahedronGeometry(100, 8), 300],
        [new THREE.IcosahedronGeometry(100, 4), 1000],
        [new THREE.IcosahedronGeometry(100, 2), 2000],
        [new THREE.IcosahedronGeometry(100, 1), 8000]
      ];

      // Store geometries for cleanup
      geometry.forEach(([geo]) => {
        geometriesRef.current.push(geo);
      });

      const material = new THREE.MeshLambertMaterial({
        color: 0xffffff,
        wireframe: true
      });

      materialRef.current = material;

      // Create LOD objects with seeded random for consistent positioning
      const seededRandom = new SeededRandom(12345); // Fixed seed for consistency
      const lodObjects: THREE.LOD[] = [];

      for (let j = 0; j < numGlobes; j++) {
        const lod = new THREE.LOD();

        for (let i = 0; i < geometry.length; i++) {
          const [geo, distance] = geometry[i];
          const mesh = new THREE.Mesh(geo, material);

          mesh.scale.set(1.5, 1.5, 1.5);
          mesh.updateMatrix();
          mesh.matrixAutoUpdate = false;
          lod.addLevel(mesh, distance);
        }

        // Scatter globes in front of the camera in a wider area
        // Camera starts at z=-1000, looking forward at z=3250
        // Create a more scattered distribution that the camera will fly through
        // x: -3000 to 3000 (wider spread, accounting for camera curves)
        // y: -2200 to 2200 (wider vertical spread, biased towards top)
        // z: 1000 to 5000 (wider range ahead of camera, avoiding very center)
        const randomX = seededRandom.next();
        const randomY = seededRandom.next();
        const randomZ = seededRandom.next();

        // Push values away from center (0.5) - creates less density in middle
        const pushAwayFromCenter = (value: number): number => {
          const distanceFromCenter = Math.abs(value - 0.5);
          const direction = value > 0.5 ? 1 : -1;
          // Apply a curve that pushes away from center more strongly
          const pushFactor = Math.pow(distanceFromCenter * 2, 0.7); // 0.7 creates moderate push

          return 0.5 + direction * pushFactor * 0.5;
        };

        // X: wider spread, still avoiding center
        lod.position.x = 6000 * (0.5 - pushAwayFromCenter(randomX));

        // Y: wider spread, biased towards top, avoiding center
        const yBias = Math.pow(randomY, 0.6); // Bias towards 1 (top)
        const yPushed = pushAwayFromCenter(yBias);

        lod.position.y = 4400 * (0.5 - yPushed);

        // Z: tunnel effect - start close to camera and extend forward
        // Camera starts around z=-1000, so globes should start near camera and extend forward
        // Create tunnel from -500 (very close) to 5000 (far ahead)
        const zStart = -500; // Start very close to camera for tunnel entrance
        const zEnd = 5000;
        const zRange = zEnd - zStart;
        // Push away from center of this range (around z=2250)
        const zNormalized = randomZ;
        const zPushed = pushAwayFromCenter(zNormalized);

        lod.position.z = zStart + zRange * zPushed; // Tunnel from -500 to 5000, less in middle
        lod.updateMatrix();
        lod.matrixAutoUpdate = false;
        scene.add(lod);
        lodObjects.push(lod);
      }

      lodObjectsRef.current = lodObjects;
      startTimeRef.current = Date.now();

      return () => {
        // Properly dispose all LOD objects
        lodObjects.forEach((lod) => {
          if (scene) {
            scene.remove(lod);
          }
          // Clear references in meshes
          lod.traverse((object) => {
            if (object instanceof THREE.Mesh) {
              object.geometry = null as any;
              object.material = null as any;
            }
          });
        });

        // Geometries and material are disposed in the cleanup at the start of the effect
        // This return cleanup is just for removing from scene
      };
    }, [scene, contextRestoreKey, numGlobes]);

    // Update speed multiplier ref when prop changes
    useEffect(() => {
      speedMultiplierRef.current = speedMultiplier;
    }, [speedMultiplier, speedMultiplierRef]);

    // Initialize camera position and look at the center of the globe cluster
    const initialXRef = useRef<number>(initialCameraPosition?.x ?? 0);
    const initialYRef = useRef<number>(initialCameraPosition?.y ?? 0);
    const initialZRef = useRef<number>(initialCameraPosition?.z ?? -1000);

    useEffect(() => {
      initialXRef.current = initialCameraPosition?.x ?? 0;
      initialYRef.current = initialCameraPosition?.y ?? 0;
      initialZRef.current = initialCameraPosition?.z ?? -1000;

      camera.position.x = initialXRef.current;
      camera.position.y = initialYRef.current;
      camera.position.z = initialZRef.current;

      // Look at the center of the globe tunnel (z=2250 is the middle of -500 to 5000 range)
      camera.lookAt(initialXRef.current, initialYRef.current, 2250);

      // Reset start time when camera is initialized to ensure consistent timing
      startTimeRef.current = Date.now();
    }, [camera, initialCameraPosition]);

    // Animation loop using useFrame
    useFrame(() => {
      // FPS limiting
      const now = performance.now();
      const frameElapsed = now - lastFrameTimeRef.current;

      if (frameElapsed < frameInterval) {
        // Skip this frame if not enough time has passed
        return;
      }

      lastFrameTimeRef.current = now - (frameElapsed % frameInterval);

      // Skip rendering if tab is not visible or component is not visible
      if (!isTabVisibleRef.current || !isVisibleRef.current) {
        return;
      }

      // Automatic subtle camera movement - flying through the scene
      const elapsed = (Date.now() - startTimeRef.current) / 1000; // Convert to seconds

      // Subtle forward movement speed (very slow for subtlety)
      const baseForwardSpeed = 80;
      const forwardSpeed = baseForwardSpeed * speedMultiplierRef.current;

      // Subtle curve using sine/cosine for organic path
      // Match the globe distribution: x: -2000 to 2000, y: -1500 to 1500 (biased top)
      const curveRadiusX = 1500; // Tighter horizontal curve to stay in globe area
      const curveRadiusY = 1000; // Tighter vertical curve, biased towards top
      const baseCurveSpeed = 0.0003;
      const curveSpeed = baseCurveSpeed * speedMultiplierRef.current;

      // Move forward along z-axis through the globe cluster (1500-4000)
      camera.position.z = initialZRef.current + forwardSpeed * elapsed;

      // Add subtle horizontal and vertical curves that stay within globe area
      // Calculate relative to initial position
      camera.position.x =
        initialXRef.current + Math.sin(elapsed * curveSpeed) * curveRadiusX;
      // Bias y towards top (positive) to match globe distribution
      camera.position.y =
        initialYRef.current +
        Math.cos(elapsed * curveSpeed * 0.7) * curveRadiusY * 0.5 +
        300; // Offset upward

      // Update lookAt to follow the path ahead
      const lookAheadZ = camera.position.z + 500; // Look slightly ahead

      camera.lookAt(camera.position.x, camera.position.y, lookAheadZ);

      // Subtle rotation to follow the curve
      camera.rotation.z = Math.sin(elapsed * curveSpeed * 0.5) * 0.05;
      camera.rotation.y = Math.sin(elapsed * curveSpeed) * 0.02;

      // Reset position if we've moved too far (loop back)
      if (camera.position.z > 5000) {
        camera.position.z = -5000;
      }
    });

    return null; // LOD objects are added directly to scene
  }
);

LODScene.displayName = 'LODScene';

const IntroSupplierLoader = memo(
  forwardRef<HTMLDivElement, IntroSupplierLoaderProps>(
    (
      {
        className,
        speedMultiplier = 1,
        initialCameraPosition,
        numGlobes = 150
      },
      ref
    ) => {
      const containerRef = useRef<HTMLDivElement | null>(null);
      const isVisibleRef = useRef<boolean>(true);
      const isTabVisibleRef = useRef<boolean>(true);
      const speedMultiplierRef = useRef<number>(speedMultiplier);

      // Merge refs: use forwarded ref if provided, otherwise use internal ref
      const setRef = (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as any).current = node;
        }
      };

      // Setup visibility detection
      useEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        // Intersection Observer to detect visibility
        const intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              isVisibleRef.current = entry.isIntersecting;
            });
          },
          {
            threshold: 0.1 // Trigger when at least 10% is visible
          }
        );

        if (container) {
          intersectionObserver.observe(container);
        }

        // Visibility API to detect tab visibility
        const handleVisibilityChange = () => {
          isTabVisibleRef.current = !document.hidden;
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        isTabVisibleRef.current = !document.hidden;

        return () => {
          document.removeEventListener(
            'visibilitychange',
            handleVisibilityChange
          );
          if (container) {
            intersectionObserver.unobserve(container);
          }
          intersectionObserver.disconnect();
        };
      }, []);

      if (!isWebGLAvailable())
        return <div ref={setRef} className={className} />;

      return (
        <div
          ref={setRef}
          className={className}
          style={{ width: '100%', height: '100%' }}
        >
          <WebGLErrorBoundary>
            <Canvas
              camera={{
                fov: 45,
                near: 1,
                far: 15000,
                position: [
                  initialCameraPosition?.x ?? 0,
                  initialCameraPosition?.y ?? 0,
                  initialCameraPosition?.z ?? -500
                ]
              }}
              gl={{ alpha: false, antialias: true }}
              style={{ width: '100%', height: '100%' }}
              dpr={window.devicePixelRatio}
            >
              <color attach="background" args={[0xffffff]} />
              <fog attach="fog" args={[0xffffff, 1, 15000]} />
              <pointLight intensity={3} color={0x000eef} position={[0, 0, 0]} />
              <directionalLight
                intensity={3}
                color={0xffffff}
                position={[0, 0, 1]}
              />
              <LODScene
                speedMultiplier={speedMultiplier}
                speedMultiplierRef={speedMultiplierRef}
                isVisibleRef={isVisibleRef}
                isTabVisibleRef={isTabVisibleRef}
                initialCameraPosition={initialCameraPosition}
                numGlobes={numGlobes}
              />
            </Canvas>
          </WebGLErrorBoundary>
        </div>
      );
    }
  ),
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    return (
      prevProps.className === nextProps.className &&
      prevProps.speedMultiplier === nextProps.speedMultiplier &&
      prevProps.numGlobes === nextProps.numGlobes &&
      prevProps.initialCameraPosition?.x ===
        nextProps.initialCameraPosition?.x &&
      prevProps.initialCameraPosition?.y ===
        nextProps.initialCameraPosition?.y &&
      prevProps.initialCameraPosition?.z === nextProps.initialCameraPosition?.z
    );
  }
);

IntroSupplierLoader.displayName = 'IntroSupplierLoader';

export default IntroSupplierLoader;
